import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Trophy, Star, Medal, Crown, Target,
  BookOpen, Calculator, FlaskConical, Loader2, Lock, Sparkles
} from 'lucide-react';

const BadgesPage = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const [child, setChild] = useState(null);
  const [allBadges, setAllBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  const badgeIcons = {
    'footprints': Target,
    'compass': BookOpen,
    'trophy': Trophy,
    'star': Star,
    'medal': Medal,
    'crown': Crown,
    'calculator': Calculator,
    'book': BookOpen,
    'flask': FlaskConical,
  };

  const badgeColors = {
    'first_steps': { bg: 'bg-blue-100', icon: 'text-blue-500', border: 'border-blue-300' },
    'explorer': { bg: 'bg-green-100', icon: 'text-green-500', border: 'border-green-300' },
    'champion': { bg: 'bg-yellow-100', icon: 'text-yellow-500', border: 'border-yellow-300' },
    'perfect_star': { bg: 'bg-orange-100', icon: 'text-orange-500', border: 'border-orange-300' },
    'level_5': { bg: 'bg-purple-100', icon: 'text-purple-500', border: 'border-purple-300' },
    'level_10': { bg: 'bg-pink-100', icon: 'text-pink-500', border: 'border-pink-300' },
    'math_whiz': { bg: 'bg-red-100', icon: 'text-red-500', border: 'border-red-300' },
    'reading_star': { bg: 'bg-emerald-100', icon: 'text-emerald-500', border: 'border-emerald-300' },
    'science_explorer': { bg: 'bg-indigo-100', icon: 'text-indigo-500', border: 'border-indigo-300' },
  };

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const [childRes, badgesRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get('/badges')
      ]);
      setChild(childRes.data);
      setAllBadges(badgesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Use default badges
      setAllBadges([
        { id: 'first_steps', name: 'First Steps', description: 'Complete your first activity!', icon: 'footprints', requirement_type: 'activities_completed', requirement_value: 1 },
        { id: 'explorer', name: 'Explorer', description: 'Complete 10 activities', icon: 'compass', requirement_type: 'activities_completed', requirement_value: 10 },
        { id: 'champion', name: 'Champion', description: 'Complete 50 activities', icon: 'trophy', requirement_type: 'activities_completed', requirement_value: 50 },
        { id: 'perfect_star', name: 'Perfect Star', description: 'Get 100% on any activity!', icon: 'star', requirement_type: 'perfect_score', requirement_value: 100 },
        { id: 'level_5', name: 'Level 5 Hero', description: 'Reach level 5', icon: 'medal', requirement_type: 'level_reached', requirement_value: 5 },
        { id: 'level_10', name: 'Level 10 Master', description: 'Reach level 10', icon: 'crown', requirement_type: 'level_reached', requirement_value: 10 },
        { id: 'math_whiz', name: 'Math Whiz', description: 'Perfect score in Math!', icon: 'calculator', requirement_type: 'perfect_score', requirement_value: 100 },
        { id: 'reading_star', name: 'Reading Star', description: 'Perfect score in Reading!', icon: 'book', requirement_type: 'perfect_score', requirement_value: 100 },
        { id: 'science_explorer', name: 'Science Explorer', description: 'Perfect score in Science!', icon: 'flask', requirement_type: 'perfect_score', requirement_value: 100 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const isEarned = (badgeId) => {
    return child?.badges?.includes(badgeId) || false;
  };

  const getIcon = (iconName) => {
    return badgeIcons[iconName] || Star;
  };

  const getColors = (badgeId) => {
    return badgeColors[badgeId] || { bg: 'bg-slate-100', icon: 'text-slate-500', border: 'border-slate-300' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-yellow-50 to-white">
        <Loader2 className="w-12 h-12 animate-spin text-yellow-500" />
      </div>
    );
  }

  const earnedBadges = allBadges.filter(b => isEarned(b.id));
  const lockedBadges = allBadges.filter(b => !isEarned(b.id));

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-yellow-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to={`/learn/${childId}`}>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Trophy className="w-6 h-6 text-yellow-500" />
                <h1 className="font-fredoka font-bold text-xl text-slate-800">
                  {child?.name}'s Badges
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-2 bg-yellow-100 rounded-full px-4 py-2">
              <Sparkles className="w-5 h-5 text-yellow-600" />
              <span className="font-bold text-yellow-700">
                {earnedBadges.length} / {allBadges.length}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Earned Badges */}
        <section className="mb-12">
          <h2 className="font-fredoka text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
            <Star className="w-6 h-6 text-yellow-500" /> Earned Badges
          </h2>
          
          {earnedBadges.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-10 h-10 text-yellow-500" />
                </div>
                <h3 className="font-bold text-xl text-slate-800 mb-2">No badges yet!</h3>
                <p className="text-slate-600 mb-4">Complete activities and assessments to earn badges!</p>
                <Link to={`/learn/${childId}`}>
                  <Button className="bg-yellow-500 hover:bg-yellow-600" data-testid="start-earning-btn">
                    Start Learning
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {earnedBadges.map((badge, index) => {
                const IconComponent = getIcon(badge.icon);
                const colors = getColors(badge.id);
                
                return (
                  <motion.div
                    key={badge.id}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className={`border-2 ${colors.border} overflow-hidden badge-earned`}>
                      <CardContent className="p-6 text-center">
                        <div className={`w-20 h-20 ${colors.bg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className={`w-10 h-10 ${colors.icon}`} />
                        </div>
                        <h3 className="font-bold text-lg text-slate-800 mb-1">{badge.name}</h3>
                        <p className="text-sm text-slate-500">{badge.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </section>

        {/* Locked Badges */}
        {lockedBadges.length > 0 && (
          <section>
            <h2 className="font-fredoka text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Lock className="w-6 h-6 text-slate-400" /> Badges to Unlock
            </h2>
            
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {lockedBadges.map((badge, index) => {
                const IconComponent = getIcon(badge.icon);
                
                return (
                  <motion.div
                    key={badge.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="border-2 border-slate-200 bg-slate-50 opacity-75">
                      <CardContent className="p-6 text-center">
                        <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                          <IconComponent className="w-10 h-10 text-slate-400" />
                          <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1">
                            <Lock className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                        <h3 className="font-bold text-lg text-slate-600 mb-1">{badge.name}</h3>
                        <p className="text-sm text-slate-400">{badge.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default BadgesPage;
